import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, AlertTriangle, XCircle, HelpCircle, RefreshCw,
  ExternalLink, Loader2, ChevronRight,
} from "lucide-react";
import { AppButton, AppModal, Callout, cn } from "../ui";

/**
 * WhatsAppSendingStatus — "can this number actually deliver a message?"
 *
 * ── The problem it solves ────────────────────────────────────────────────────
 * A WhatsApp send returns 200 the moment Meta *accepts* the payload. Everything
 * that stops delivery after that — business verification not finished, no
 * payment method on the WhatsApp Business Account, a number still in review, a
 * quality suspension — is invisible here and shows up only as messages that
 * quietly never arrive. The admin's own reading of the screen is "we sent it",
 * and the fix lives in a Meta console they were never told to open.
 *
 * ── The shape of the answer ──────────────────────────────────────────────────
 * A healthy account gets ONE quiet line. It is not news, and a green banner
 * repeated above every screen is the fastest way to teach people not to read
 * banners. A limited or blocked account gets a tinted strip naming the single
 * worst problem, and one press opens the full list with Meta's own wording, the
 * fix, and a link straight to the console page that owns it.
 *
 * ⚠️ It never gates sending. Meta's verdict is occasionally stale, and refusing
 * to send on our reading of it would take an institute's messaging down on our
 * word alone. It informs; the Send button stays the Send button.
 *
 * Props:
 *   fetchHealth(force) → Promise<payload>   see vc_be/services/whatsappHealthService.js
 *   variant  — "strip" (inbox sidebar, default) | "card" (a settings page)
 *   className
 */

/* Per-verdict presentation. `canSend` is Meta's vocabulary, kept verbatim so a
   value we have not seen falls through to "unknown" rather than to "healthy" —
   the safe direction for a wrong guess. */
const VERDICTS = {
  AVAILABLE: {
    tone: "success",
    Icon: CheckCircle2,
    label: "Sending is healthy",
    strip: "tw-border-line tw-bg-surface-alt tw-text-content-muted",
    dot: "tw-text-success",
  },
  LIMITED: {
    tone: "warning",
    Icon: AlertTriangle,
    label: "Sending is limited",
    strip: "tw-border-warning/30 tw-bg-warning/10 tw-text-content",
    dot: "tw-text-warning",
  },
  BLOCKED: {
    tone: "danger",
    Icon: XCircle,
    label: "Sending is blocked",
    strip: "tw-border-danger/30 tw-bg-danger/10 tw-text-content",
    dot: "tw-text-danger",
  },
  UNKNOWN: {
    tone: "info",
    Icon: HelpCircle,
    label: "Sending status unknown",
    strip: "tw-border-line tw-bg-surface-alt tw-text-content-muted",
    dot: "tw-text-content-subtle",
  },
};

const verdictOf = (canSend) => VERDICTS[canSend] || VERDICTS.UNKNOWN;

const SEVERITY_TONE = { blocked: "danger", limited: "warning", warning: "warning" };

/* Meta's tier names are machine vocabulary. A person reading a settings page
   wants the number of new conversations a day it actually buys them. */
const TIER_LABELS = {
  TIER_250: "250 new conversations / day",
  TIER_1K: "1,000 new conversations / day",
  TIER_10K: "10,000 new conversations / day",
  TIER_100K: "100,000 new conversations / day",
  TIER_UNLIMITED: "Unlimited",
  NOT_APPLICABLE: "Not applicable",
};

const prettyTier = (t) => TIER_LABELS[t] || t || "—";

const prettyTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

/* A blocker, as the modal lists it: what is wrong, what Meta said about it, what
   to do, and the console page that owns the fix. */
const BlockerRow = ({ blocker }) => {
  const tone = SEVERITY_TONE[blocker.severity] || "info";
  const Icon = blocker.severity === "blocked" ? XCircle : AlertTriangle;
  return (
    <div
      className={cn(
        "tw-rounded-sm tw-border tw-border-solid tw-p-3",
        tone === "danger"
          ? "tw-border-danger/30 tw-bg-danger/10"
          : "tw-border-warning/30 tw-bg-warning/10"
      )}
    >
      <div className="tw-flex tw-items-start tw-gap-2">
        <Icon
          size={15}
          className={cn("tw-mt-0.5 tw-shrink-0", tone === "danger" ? "tw-text-danger" : "tw-text-warning")}
        />
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-mb-1 tw-text-sm tw-font-semibold tw-text-content">{blocker.title}</p>

          {/* Meta's own wording is kept even when we have a friendlier title:
              it is what the owner will see quoted back at them in the console. */}
          {blocker.detail && blocker.detail !== blocker.title && (
            <p className="tw-mb-1 tw-text-xs tw-leading-normal tw-text-content-muted">{blocker.detail}</p>
          )}

          {blocker.solution && (
            <p className="tw-mb-2 tw-text-xs tw-leading-normal tw-text-content">{blocker.solution}</p>
          )}

          {blocker.link && (
            <a
              className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary tw-no-underline hover:tw-underline"
              href={blocker.link}
              target="_blank"
              rel="noreferrer noopener"
            >
              {blocker.linkLabel || "Open in Meta"} <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Fact = ({ label, value, tone }) => (
  <div className="tw-min-w-0">
    <dt className="tw-mb-0.5 tw-text-xs tw-text-content-subtle">{label}</dt>
    <dd
      className={cn(
        "tw-mb-0 tw-truncate tw-text-sm tw-text-content",
        tone === "success" && "tw-text-success-fg",
        tone === "warning" && "tw-text-warning-fg",
        tone === "danger" && "tw-text-danger-fg"
      )}
    >
      {value || "—"}
    </dd>
  </div>
);

/* Meta's raw enum values, softened. A blank stays blank rather than becoming a
   confident "Unknown" — we did not read the field, which is not the same as
   Meta telling us it is unset. */
const humanise = (v) =>
  (v ? String(v).replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase()) : "");

const WhatsAppSendingStatus = ({ fetchHealth, variant = "strip", className }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (force) => {
    if (force) setRefreshing(true);
    try {
      const data = await fetchHealth(force);
      setHealth(data);
      setError("");
    } catch (err) {
      // A failed health read is not a failed account. Say which one happened.
      setError(err?.response?.data?.message || "Could not read the sending status");
    }
    setLoading(false);
    setRefreshing(false);
  }, [fetchHealth]);

  useEffect(() => { load(false); }, [load]);

  /* Not connected at all is a different screen's problem — the Config tab
     already says so, loudly, and a second copy here would be noise. */
  if (loading || !health || health.connected === false) {
    if (variant === "card" && !loading) {
      return (
        <Callout tone="info" className={className}>
          WhatsApp is not connected, so nothing can be sent from this account yet.
        </Callout>
      );
    }
    return null;
  }

  const v = verdictOf(health.canSend);
  const blockers = health.blockers || [];
  const worst = blockers[0];
  const healthy = health.canSend === "AVAILABLE" && !blockers.length;

  const summary = healthy
    ? `Sending from ${health.displayPhoneNumber || "your WhatsApp number"}`
    : (worst?.title || v.label);

  /* ── The strip ────────────────────────────────────────────────────────────
     One row, and the whole row is the control: a problem worth showing is worth
     making easy to open, and a separate "details" link beside a tinted banner
     is a target people miss. */
  const strip = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={cn(
        "tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-px-3 tw-py-2 tw-text-left",
        v.strip,
        className
      )}
      title="WhatsApp sending status"
    >
      <v.Icon size={14} className={cn("tw-shrink-0", v.dot)} />
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-leading-normal">
        {summary}
      </span>
      {!healthy && blockers.length > 1 && (
        <span className="tw-shrink-0 tw-rounded-full tw-bg-surface-raised tw-px-1.5 tw-py-0.5 tw-text-xs tw-text-content-muted">
          +{blockers.length - 1}
        </span>
      )}
      <ChevronRight size={13} className="tw-shrink-0 tw-opacity-60" />
    </button>
  );

  /* ── The card ─────────────────────────────────────────────────────────────
     Same information, for a settings page where it is the subject rather than
     an aside. */
  const card = (
    <div
      className={cn(
        "tw-rounded tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-4",
        className
      )}
    >
      <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-3">
        <v.Icon size={20} className={cn("tw-mt-0.5 tw-shrink-0", v.dot)} />
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-mb-0.5 tw-text-base tw-font-semibold tw-text-content">{v.label}</p>
          <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
            {health.displayPhoneNumber || "WhatsApp Business number"}
            {health.checkedAt ? ` · checked ${prettyTime(health.checkedAt)}` : ""}
          </p>
        </div>
        <AppButton size="sm" variant="outline" onClick={() => setOpen(true)}>
          View details
        </AppButton>
      </div>

      {!healthy && worst && (
        <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content">{worst.title}</p>
      )}
    </div>
  );

  return (
    <>
      {variant === "card" ? card : strip}

      <AppModal
        show={open}
        onHide={() => setOpen(false)}
        title="WhatsApp sending status"
        size="lg"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-text-xs tw-text-content-subtle">
              {health.checkedAt ? `Checked ${prettyTime(health.checkedAt)}` : ""}
              {health.cached ? " · cached" : ""}
            </span>
            <div className="tw-flex tw-gap-2">
              <AppButton size="sm" variant="outline" onClick={() => load(true)} disabled={refreshing}>
                {refreshing
                  ? <><Loader2 size={13} className="tw-animate-spin" /> Checking…</>
                  : <><RefreshCw size={13} /> Re-check now</>}
              </AppButton>
              <AppButton size="sm" onClick={() => setOpen(false)}>Close</AppButton>
            </div>
          </div>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          {error && <Callout tone="danger" role="alert">{error}</Callout>}

          {/* The verdict, stated once, in words. */}
          <div className="tw-flex tw-items-start tw-gap-2">
            <v.Icon size={18} className={cn("tw-mt-0.5 tw-shrink-0", v.dot)} />
            <div>
              <p className="tw-mb-0.5 tw-text-base tw-font-semibold tw-text-content">{v.label}</p>
              <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
                {healthy
                  ? "Meta reports no restriction on this account. Messages you send should be delivered."
                  : "Outbound messages can be accepted and then dropped while this is unresolved."}
              </p>
            </div>
          </div>

          {/* What our own webhook has seen fail. This is what turns Meta's
              verdict from a fact about the account into a cost being paid now. */}
          {health.failedLast24h > 0 && (
            <Callout tone="warning">
              {health.failedLast24h} outbound message{health.failedLast24h === 1 ? "" : "s"} failed
              in the last 24 hours
              {health.recentFailures?.length
                ? `. Most common error: ${health.recentFailures[0].code || "unknown"}${
                  health.recentFailures[0].message ? ` — ${health.recentFailures[0].message}` : ""}`
                : "."}
            </Callout>
          )}

          {blockers.length > 0 && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-content">
                What to fix, in Meta
              </p>
              {!health.canFix && (
                <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
                  These are changed in the Meta Business account, which your institute
                  administrator owns. Send them this list.
                </p>
              )}
              {blockers.map((b) => <BlockerRow key={b.id} blocker={b} />)}
            </div>
          )}

          {healthy && (
            <Callout tone="success">
              Business verification, account review and the sending number all passed
              Meta&apos;s checks.
            </Callout>
          )}

          {/* The underlying facts, for the person who has to go and act on them. */}
          <div>
            <p className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-content">Account</p>
            <dl className="tw-mb-0 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 sm:tw-grid-cols-3">
              <Fact label="Number" value={health.displayPhoneNumber} />
              <Fact
                label="Number status"
                value={humanise(health.phone?.status)}
                tone={health.phone?.status === "CONNECTED" ? "success" : "danger"}
              />
              <Fact
                label="Display name"
                value={humanise(health.phone?.nameStatus)}
                tone={health.phone?.nameStatus === "APPROVED" ? "success" : "warning"}
              />
              <Fact
                label="Business verification"
                value={humanise(health.waba?.businessVerificationStatus)}
                tone={health.waba?.businessVerificationStatus === "verified" ? "success" : "warning"}
              />
              <Fact
                label="Account review"
                value={humanise(health.waba?.accountReviewStatus)}
                tone={health.waba?.accountReviewStatus === "APPROVED" ? "success" : "warning"}
              />
              <Fact
                label="Quality rating"
                value={humanise(health.phone?.qualityRating)}
                tone={
                  health.phone?.qualityRating === "GREEN" ? "success"
                    : health.phone?.qualityRating === "RED" ? "danger" : "warning"
                }
              />
              <Fact
                label="Messaging limit"
                value={prettyTier(health.phone?.messagingLimitTier || health.waba?.messagingLimitTier)}
              />
              {health.wabaId && <Fact label="WhatsApp Business Account" value={health.wabaId} />}
              {health.phoneNumberId && <Fact label="Phone number ID" value={health.phoneNumberId} />}
            </dl>
          </div>

          {/* Meta's health verdict needs a token holding a role on the account.
              When it is missing, everything above was derived by us from the
              plain fields — say so rather than implying Meta cleared it. */}
          {health.metaHealthAvailable === false && (
            <Callout tone="info">
              Meta did not return an account health verdict for this token, so the checks
              above are read from the account&apos;s own settings instead. They can miss a
              restriction Meta has applied but not published on these fields.
            </Callout>
          )}
        </div>
      </AppModal>
    </>
  );
};

export default WhatsAppSendingStatus;
